import React, { useState } from "react";
import {
  Typography, Grid, Box, Paper, Tooltip, IconButton,
  Popover, Button,
} from "@mui/material";
import { useQuery, useMutation } from "@apollo/client";
import EditIcon from "@mui/icons-material/Edit";
import LockIcon from "@mui/icons-material/Lock";
import { CalendarToday } from "@mui/icons-material";
import {
  GET_STAFF_DETAILS,
  UPDATE_EMPLOYEE_DETAILS,
} from "../../../../../../../apis/organization-apis/people-apis/peopleApis";
import ConfirmSnackbar from "../../../../../../../components/Snackbar/ConfirmSnackbar";
import EditRoleDialog from "./EditRoleDialog";
import EditManagerDialog from "./EditManagerDialog";
import DatePicker from "../../../../../../../components/DatePicker/DatePicker";
import { MasterLookupItemsType, AccountCategoryKey } from "../../../../../../../utils/enums";
import { formatDate, formatDateForInput } from "../../../../../../../utils/dateUtils";

const FUTURE_TERMINATION_DATE_MESSAGE =
  "Future termination dates aren't allowed. Setting one early would revoke the employee's Regodit access before they can complete required policies and training.";

const StaffInfoCard = ({
  staffMember,
  originalEmployee,
  refetch,
  hasHrmsIntegration = false,
  onEditStartDate,
  onScopeChange,
  onScopeSaveSuccess,
  onScopeSaveError,
}) => {
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false);
  const [editManagerDialogOpen, setEditManagerDialogOpen] = useState(false);

  // Join Date popover
  const [joinDateAnchor, setJoinDateAnchor] = useState(null);
  const [joinDateValue, setJoinDateValue] = useState("");

  // Termination Date popover
  const [termDateAnchor, setTermDateAnchor] = useState(null);
  const [termDateValue, setTermDateValue] = useState("");

  const [updateEmployee, { loading: saving }] = useMutation(UPDATE_EMPLOYEE_DETAILS);
  const { data: employeesData } = useQuery(GET_STAFF_DETAILS);

  const hasTerminationDate = !!originalEmployee?.termination_date;
  const userFetchingInfo = staffMember?.user_fetching_info || originalEmployee?.user_fetching_info;
  const isHrmsSource = hasHrmsIntegration && userFetchingInfo?.key === MasterLookupItemsType.HRMS_PROVIDER;
  const joinDate = staffMember?.joinDate || originalEmployee?.start_date;

  const getEmail = () =>
    originalEmployee?.work_email ||
    originalEmployee?.emails?.find((e) => e.type === "work")?.email ||
    originalEmployee?.emails?.[0]?.email ||
    "";

  // ── Join Date save ──
  const handleSaveJoinDate = async () => {
    const email = getEmail();
    if (!email || !joinDateValue) return;
    try {
      const res = await updateEmployee({
        variables: { input: [{ id: originalEmployee.id, email, start_date: joinDateValue }] },
      });
      if (res?.data?.updateEmployeeDetails?.success) {
        setSnackbarInfo({ message: "Join date updated", vertical: "top", horizontal: "right" });
        setJoinDateAnchor(null);
        if (refetch) refetch();
      } else {
        setSnackbarInfo({
          message: res?.data?.updateEmployeeDetails?.message || "Failed to update join date",
          vertical: "top", horizontal: "right",
        });
      }
    } catch (err) {
      setSnackbarInfo({ message: err?.message || "Failed to update join date", vertical: "top", horizontal: "right" });
    }
  };

  // ── Termination Date save ──
  const handleSaveTermDate = async () => {
    const email = getEmail();
    if (!email) return;

    if (termDateValue) {
      const today = formatDateForInput(new Date());
      if (termDateValue > today) {
        setSnackbarInfo({
          message: FUTURE_TERMINATION_DATE_MESSAGE,
          vertical: "top", horizontal: "right",
        });
        return;
      }
    }

    if (termDateValue && originalEmployee?.start_date) {
      const start = new Date(originalEmployee.start_date);
      const term = new Date(termDateValue);
      if (start > term) {
        setSnackbarInfo({
          message: "Termination date cannot be before the join date",
          vertical: "top", horizontal: "right",
        });
        return;
      }
    }

    const isClearing = !termDateValue && hasTerminationDate;
    const input = {
      id: originalEmployee.id,
      email,
      termination_date: termDateValue || null,
    };

    if (termDateValue) {
      input.in_scope = false;
      input.employee_type = MasterLookupItemsType.NOT_IN_SCOPE_USER_CATEGORY;
      input.employee_key = AccountCategoryKey.OFFBOARDED_USER;
      onScopeChange?.(false);
    } else if (isClearing) {
      input.in_scope = true;
      input.employee_type = MasterLookupItemsType.IN_SCOPE_USER_CATEGORY;
      input.employee_key = AccountCategoryKey.ACTIVE_USER;
      input.scope_reason = null;
      onScopeChange?.(true);
    }

    try {
      const res = await updateEmployee({ variables: { input: [input] } });
      if (res?.data?.updateEmployeeDetails?.success) {
        setSnackbarInfo({
          message: termDateValue
            ? "Termination date saved. Employee marked as Out of Scope."
            : "Termination date cleared. Employee marked as In Scope.",
          vertical: "top", horizontal: "right",
        });
        onScopeSaveSuccess?.();
        setTermDateAnchor(null);
        if (refetch) refetch();
      } else {
        onScopeSaveError?.();
        setSnackbarInfo({
          message: res?.data?.updateEmployeeDetails?.message || "Failed to update termination date",
          vertical: "top", horizontal: "right",
        });
      }
    } catch (err) {
      onScopeSaveError?.();
      setSnackbarInfo({ message: err?.message || "Failed to update termination date", vertical: "top", horizontal: "right" });
    }
  };

  if (!staffMember) return null;

  return (
    <Box sx={{ mt: 3, mx: 4 }}>
      <Paper
        elevation={0}
        sx={{ p: 3, backgroundColor: "white", borderRadius: 2, border: "1px solid #e5e7eb" }}
      >
        <Grid
          container
          spacing={4}
          sx={{
            "& > .MuiGrid-item": {
              flexBasis: "20%",
              maxWidth: "20%",
              "@media (max-width: 899px)": { flexBasis: "50%", maxWidth: "50%" },
              "@media (max-width: 599px)": { flexBasis: "100%", maxWidth: "100%" },
            },
          }}
        >
          {/* Role */}
          <Grid item>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                <Typography variant="caption" color="#64748b" fontWeight={600}>Role</Typography>
                <Tooltip title={isHrmsSource ? "Managed by HRMS" : "Edit Role"} arrow>
                  <span>
                    <IconButton size="small" onClick={() => setEditRoleDialogOpen(true)} disabled={isHrmsSource}
                      sx={{ p: 0.25, color: isHrmsSource ? "#cbd5e1" : "#64748b", "&:hover": { color: "#031B89", backgroundColor: "#f0f4ff" }, "&.Mui-disabled": { color: "#cbd5e1" } }}>
                      <EditIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              <Typography variant="body1" fontWeight={600}
                color={staffMember?.role === "Not Assigned" || staffMember?.role === "-" ? "#f44336" : "#031B89"}>
                {staffMember?.role === "-" ? "Not Assigned" : staffMember?.role || "Not Assigned"}
              </Typography>
            </Box>
          </Grid>

          {/* Reporting Manager */}
          <Grid item>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                <Typography variant="caption" color="#64748b" fontWeight={600}>Reporting Manager</Typography>
                <Tooltip title={isHrmsSource ? "Managed by HRMS" : "Edit Manager"} arrow>
                  <span>
                    <IconButton size="small" onClick={() => setEditManagerDialogOpen(true)} disabled={isHrmsSource}
                      sx={{ p: 0.25, color: isHrmsSource ? "#cbd5e1" : "#64748b", "&:hover": { color: "#031B89", backgroundColor: "#f0f4ff" }, "&.Mui-disabled": { color: "#cbd5e1" } }}>
                      <EditIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              {staffMember?.manager !== "Not Assigned" && staffMember?.manager !== "-" ? (
                <Typography variant="body1" fontWeight={600} color="#031B89">{staffMember?.manager}</Typography>
              ) : (
                <Typography variant="body1" fontWeight={600} color="#f44336">Not Assigned</Typography>
              )}
            </Box>
          </Grid>

          {/* Join Date */}
          <Grid item>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                <CalendarToday sx={{ color: "#64748b", fontSize: 13 }} />
                <Typography variant="caption" color="#64748b" fontWeight={600}>Join Date</Typography>
                <Tooltip title={isHrmsSource ? "Managed by HRMS" : "Edit Join Date"} arrow>
                  <span>
                    <IconButton
                      size="small"
                      disabled={isHrmsSource}
                      onClick={(e) => {
                        setJoinDateValue(formatDateForInput(joinDate) || "");
                        setJoinDateAnchor(e.currentTarget);
                      }}
                      sx={{ p: 0.25, color: isHrmsSource ? "#cbd5e1" : "#64748b", "&:hover": { color: "#031B89", backgroundColor: "#f0f4ff" }, "&.Mui-disabled": { color: "#cbd5e1" } }}
                    >
                      <EditIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              <Typography variant="body1" fontWeight={600} color="#031B89">
                {joinDate && joinDate !== "-" ? formatDate(joinDate) || "Not Available" : "Not Available"}
              </Typography>
            </Box>
          </Grid>

          {/* Termination Date */}
          <Grid item>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                <CalendarToday sx={{ color: "#64748b", fontSize: 13 }} />
                <Typography variant="caption" color="#64748b" fontWeight={600}>Termination Date</Typography>
                {isHrmsSource ? (
                  <Tooltip title="Managed by HRMS" arrow>
                    <span>
                      <IconButton
                        size="small"
                        disabled={true}
                        sx={{ p: 0.25, color: "#cbd5e1", "&:hover": { color: "#031B89", backgroundColor: "#f0f4ff" }, "&.Mui-disabled": { color: "#cbd5e1" } }}
                      >
                        <EditIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : (
                  <Tooltip title={hasTerminationDate ? "Edit or clear termination date" : "Set termination date"} arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        setTermDateValue(formatDateForInput(originalEmployee?.termination_date) || "");
                        setTermDateAnchor(e.currentTarget);
                      }}
                      sx={{ p: 0.25, color: "#64748b", "&:hover": { color: "#031B89", backgroundColor: "#f0f4ff" } }}
                    >
                      <EditIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Typography variant="body1" fontWeight={600} color={hasTerminationDate ? "#ef4444" : "#9ca3af"}>
                {hasTerminationDate ? formatDate(originalEmployee.termination_date) : "Not set"}
              </Typography>
            </Box>
          </Grid>

          {/* Source */}
          <Grid item>
            <Box>
              <Typography variant="caption" color="#64748b" fontWeight={600} sx={{ display: "block", mb: 0.5 }}>Source</Typography>
              {(() => {
                let sourceText = "Manual";
                let tooltipText = "Manually added employee";
                if (userFetchingInfo?.key === MasterLookupItemsType.HRMS_PROVIDER) {
                  sourceText = userFetchingInfo?.name || "HRMS";
                  tooltipText = `Synced from HRMS ${userFetchingInfo?.name || "HRMS"}`;
                } else if (userFetchingInfo?.key === MasterLookupItemsType.IDP_PROVIDER) {
                  sourceText = userFetchingInfo?.name || "Source";
                  tooltipText = `Synced from ${userFetchingInfo?.name || "data source"}`;
                }
                return (
                  <Tooltip title={tooltipText} arrow>
                    <Typography variant="body1" fontWeight={600} color="#031B89">{sourceText}</Typography>
                  </Tooltip>
                );
              })()}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Join Date popover */}
      <Popover
        open={Boolean(joinDateAnchor)}
        anchorEl={joinDateAnchor}
        onClose={() => setJoinDateAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { borderRadius: 2, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", p: 2, width: 320, mt: 0.5 } }}
      >
        <Typography variant="body2" fontWeight={600} color="#111827" sx={{ mb: 1.5 }}>Edit Join Date</Typography>
        <DatePicker
          fullWidth
          value={joinDateValue}
          onChange={setJoinDateValue}
          maxDate={formatDateForInput(originalEmployee?.termination_date)}
          placeholder="Select join date"
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1.5 }}>
          <Button size="small" onClick={() => setJoinDateAnchor(null)} sx={{ color: "#6b7280" }}>Cancel</Button>
          <Button size="small" variant="contained" onClick={handleSaveJoinDate} disabled={saving || !joinDateValue}>
            Save
          </Button>
        </Box>
      </Popover>

      {/* Termination Date popover */}
      <Popover
        open={Boolean(termDateAnchor)}
        anchorEl={termDateAnchor}
        onClose={() => setTermDateAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { borderRadius: 2, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", p: 2, width: 320, mt: 0.5 } }}
      >
        <Typography variant="body2" fontWeight={600} color="#111827" sx={{ mb: 1.5 }}>
          {hasTerminationDate ? "Edit Termination Date" : "Set Termination Date"}
        </Typography>
        <DatePicker
          fullWidth
          value={termDateValue}
          onChange={(v) => setTermDateValue(v || "")}
          minDate={formatDateForInput(joinDate)}
          maxDate={formatDateForInput(new Date())}
          placeholder="Select termination date"
          clearable
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1.5 }}>
          <Button size="small" onClick={() => setTermDateAnchor(null)} sx={{ color: "#6b7280" }}>Cancel</Button>
          <Button size="small" variant="contained" onClick={handleSaveTermDate} disabled={saving}>
            Save
          </Button>
        </Box>
      </Popover>

      <EditRoleDialog
        open={editRoleDialogOpen}
        onClose={() => setEditRoleDialogOpen(false)}
        employee={originalEmployee}
        currentRole={staffMember?.role}
        refetch={refetch}
        hasHrmsIntegration={isHrmsSource}
      />
      <EditManagerDialog
        open={editManagerDialogOpen}
        onClose={() => setEditManagerDialogOpen(false)}
        employee={originalEmployee}
        currentManager={staffMember?.manager}
        currentManagerEmail={staffMember?.managerEmail}
        employees={employeesData?.getEmployees?.data || []}
        refetch={refetch}
        hasHrmsIntegration={isHrmsSource}
      />
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </Box>
  );
};

export default StaffInfoCard;
